
import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSidebar } from './Sidebar/SidebarContext'

// ─── Context ─────────────────────────────────────────────────────────────────

const ScreenContext = createContext({
    loading: false,
    setLoading: () => {},
    // sidebar — resolved at runtime via the inner hook
    collapsed: false,
    toggleSidebar: () => {},
    setCollapsed: () => {},
    // full-width mode — pages set this to expand content and auto-close sidebar
    fullWidth: false,
    setFullWidth: () => {},
    // ref to the scrollable cs-body DOM node
    bodyRef: { current: null },
    // ref to the scrollable cs-sidebar DOM node
    sidebarRef: { current: null },
    // breadcrumb: array of { label, href?, icon? } | null
    breadcrumb: null,
    setBreadcrumb: () => {},
    // viewport background effect — pass an image URL or null to clear
    backgroundEffect: null,
    setBackgroundEffect: () => {},
    // snackbar — bridges to window.setSnackbar (registered by legacy useScreen hook)
    setSnackbar: () => {},
})

// ─── Provider ─────────────────────────────────────────────────────────────────
// Must be rendered INSIDE <SidebarProvider> so useSidebar() resolves correctly.

function ScreenContextBridge({ children }) {
    const [loading, setLoading] = useState(false)
    const [fullWidth, setFullWidth] = useState(false)
    const [breadcrumb, setBreadcrumb] = useState(null)
    const [backgroundEffect, setBackgroundEffect] = useState(null)
    const bodyRef = useRef(null)
    const sidebarRef = useRef(null)

    const sidebar = useSidebar()

    // Auto-collapse the sidebar when a page enters full-width mode
    useEffect(() => {
        if (fullWidth) sidebar.setCollapsed(true)
    }, [fullWidth])

    // Stable setter references
    const stableSetLoading = useCallback(setLoading, [])
    const stableSetFullWidth = useCallback(setFullWidth, [])
    const stableSetBreadcrumb = useCallback(setBreadcrumb, [])
    const stableSetBackgroundEffect = useCallback(setBackgroundEffect, [])
    const stableSetSnackbar = useCallback((message) => window.setSnackbar?.(message), [])

    // Memoize the value object so consumers only re-render when their
    // specific slice of state actually changes.
    const value = useMemo(() => ({
        loading,
        setLoading: stableSetLoading,
        collapsed: sidebar.collapsed,
        toggleSidebar: sidebar.toggleSidebar,
        setCollapsed: sidebar.setCollapsed,
        fullWidth,
        setFullWidth: stableSetFullWidth,
        bodyRef,
        sidebarRef,
        breadcrumb,
        setBreadcrumb: stableSetBreadcrumb,
        backgroundEffect,
        setBackgroundEffect: stableSetBackgroundEffect,
        setSnackbar: stableSetSnackbar,
    }), [
        loading,
        sidebar.collapsed,
        sidebar.toggleSidebar,
        sidebar.setCollapsed,
        fullWidth,
        breadcrumb,
        backgroundEffect,
        // refs and stable callbacks are safe to omit from deps
    ])

    return (
        <ScreenContext.Provider value={value}>
            {children}
        </ScreenContext.Provider>
    )
}

export { ScreenContextBridge }

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useScreen() {
    return useContext(ScreenContext)
}
